import { InteractivePreview, IsDesignMode } from "../UserInterfaceModule/Utils/runtimeDetection";
import { ABORT_ERROR_NAME, HTTP_CACHE_MODE, HTTP_HEADER, HTTP_METHOD, HTTP_STATUS, MEDIA_TYPE } from "./HttpProtocol";

type Transport = { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
const maximumEntries = 8;
const maximumBodyLength = 2 * 1024 * 1024;
const lifetimeMs = 5 * 60 * 1000;
const sessionPath = /\/Chat\/[^/]+\/[^/]+\/Sessions\/[^/?]+(?:\/Updates)?(?:\?|$)/;

/** Client-local, bounded response reuse; every reuse still makes an authorized conditional request. */
export function chatConditionalTransport(transport: Transport, bypass = IsDesignMode || InteractivePreview.enabled): Transport {
  const entries = new Map<string, { etag: string; body: string; expires: number; contentType: string }>();
  return {
    fetch: async (url, init) => {
      const method = init?.method?.toUpperCase() ?? HTTP_METHOD.get;
      if (bypass || init?.cache === HTTP_CACHE_MODE.noStore || method !== HTTP_METHOD.get || !sessionPath.test(String(url))) {
        const response = await transport.fetch(url, init);
        if (method !== HTTP_METHOD.get && response.status >= HTTP_STATUS.ok && response.status < HTTP_STATUS.successUpperBound) entries.clear();
        return response;
      }
      const headers = new Headers(init?.headers);
      // Token/context changes cannot reuse another identity's response, even within a retained client.
      const key = `${headers.get(HTTP_HEADER.authorization) ?? ""}:${String(url)}`;
      const previous = entries.get(key);
      const cached = previous && previous.expires > Date.now() ? previous : undefined;
      if (cached) headers.set(HTTP_HEADER.ifNoneMatch, cached.etag);
      const response = await transport.fetch(url, { ...init, headers });
      if (response.status === HTTP_STATUS.notModified && cached) {
        if (init?.signal?.aborted) throw new DOMException("Chat read cancelled.", ABORT_ERROR_NAME);
        const replayHeaders = new Headers(response.headers);
        replayHeaders.set(HTTP_HEADER.contentType, cached.contentType);
        return new Response(cached.body, { status: HTTP_STATUS.ok, headers: replayHeaders });
      }
      if (response.status !== HTTP_STATUS.ok) {
        entries.delete(key);
        return response;
      }
      const etag = response.headers.get(HTTP_HEADER.etag);
      if (!etag) {
        entries.delete(key);
        return response;
      }
      const body = await response.clone().text();
      entries.delete(key);
      if (!init?.signal?.aborted && body.length <= maximumBodyLength) {
        entries.set(key, { etag, body, expires: Date.now() + lifetimeMs, contentType: response.headers.get(HTTP_HEADER.contentType) ?? MEDIA_TYPE.json });
        if (entries.size > maximumEntries) entries.delete(entries.keys().next().value!);
      }
      return response;
    },
  };
}
